import React, { useEffect, useState } from 'react';
import { Coins, Plus, Save, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';
import { GCOMPRIS_ACTIVITIES } from '../../data/gcomprisCatalog';
import SearchableSelect from '../common/SearchableSelect';

const MAX_COINS = 10000;
const MAX_LEVEL = 500;
const MAX_ROWS = 500;
const isBlank = (value) => value === '' || value === null || value === undefined;
const isWhole = (value, min, max) => !isBlank(value) && Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= max;
const numberOrBlank = (value) => (value === '' ? '' : Number(value));
let lastRowId = 0;
// game '' = All games, level '' = every level of the game.
const levelRow = (game, level, coins) => ({ id: (lastRowId += 1), game, level, coins });

const GAMES = [...GCOMPRIS_ACTIVITIES].sort((a, b) => a.title.localeCompare(b.title));
const gameTitle = (name) => GCOMPRIS_ACTIVITIES.find((activity) => activity.name === name)?.title || name;
// "level 5", "every level of Learning clock" - for the messages.
const rowLabel = (row) => `${isBlank(row.level) ? 'every level' : `level ${row.level}`}${row.game ? ` of ${gameTitle(row.game)}` : ''}`;
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
// "Each level pays: Level 1 → 20, Level 2 → 30, Level 3 → 40 coins …" (level N = per game + per level up × (N - 1)).
const exampleLevels = (perGame, perLevel) => {
  const base = isWhole(perGame, 0, MAX_COINS) ? Number(perGame) : 0;
  const step = isWhole(perLevel, 0, MAX_COINS) ? Number(perLevel) : 0;
  const levels = [1, 2, 3].map((level) => `Level ${level} → ${base + step * (level - 1)}`).join(', ');
  return `Each level pays: ${levels} coins${step ? ' …' : ''}`;
};
// Nothing has to be filled in: a custom row without coins is simply not saved.
const rowsToSave = (levels) => levels.filter((row) => !isBlank(row.coins));

// What stops the settings from being saved ('' when they can be).
const problemWith = ({ perGame, perLevel, levels }) => {
  if (!isBlank(perGame) && !isWhole(perGame, 0, MAX_COINS)) return 'Coins per game must be a whole number from 0 to 10000';
  if (!isBlank(perLevel) && !isWhole(perLevel, 0, MAX_COINS)) return 'Coins per level up must be a whole number from 0 to 10000';
  const rows = rowsToSave(levels);
  const noTarget = rows.find((row) => !row.game && isBlank(row.level));
  if (noTarget) return `Pick a game or a level for the ${noTarget.coins} coins`;
  const badLevel = rows.find((row) => !isBlank(row.level) && !isWhole(row.level, 1, MAX_LEVEL));
  if (badLevel) return `Custom levels must be whole numbers from 1 to ${MAX_LEVEL}`;
  const badCoins = rows.find((row) => !isWhole(row.coins, 0, MAX_COINS));
  if (badCoins) return `Coins for ${rowLabel(badCoins)} must be a whole number from 0 to 10000`;
  const sameTarget = (a, b) => a.game === b.game && String(a.level) === String(b.level);
  const repeated = rows.find((row, i) => rows.findIndex((other) => sameTarget(other, row)) !== i);
  if (repeated) return `${capitalize(rowLabel(repeated))} is added more than once`;
  return '';
};

const inputClass = 'mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 text-lg font-bold focus:border-purple-500 focus:ring-2 focus:ring-purple-200';
const rowInputClass = 'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-bold focus:border-purple-500 focus:ring-2 focus:ring-purple-200';

/**
 * GCompris coin settings (admin, Courses › Computer Apps › GCompris). The first time a student
 * completes level N of a game they get the coins per game + the coins per level up × (N - 1)
 * (20 and 10 -> 20, 30, 40...), unless a custom amount replaces it: for a level in every game, a
 * level of one game or every level of one game. Nothing is required: an empty box saves 0.
 */
export default function GComprisRewardModal({ isOpen, onClose }) {
  const [perGame, setPerGame] = useState(0);
  const [perLevel, setPerLevel] = useState(0);
  const [levels, setLevels] = useState([]); // [{ id, game, level, coins }]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/gcompris/settings/reward');
        setPerGame(Number(response.data?.coinsPerGame) || 0);
        setPerLevel(Number(response.data?.coinsPerNewLevel) || 0);
        setLevels((response.data?.levelCoins || []).map((item) => levelRow(item.activityName || '', item.level ?? '', item.coins)));
      } catch (error) {
        toast.error('Failed to load GCompris reward settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen]);

  if (!isOpen) return null;

  const addLevel = () => {
    const used = levels.map((item) => Number(item.level)).filter(Number.isFinite);
    setLevels((list) => [...list, levelRow('', Math.min(MAX_LEVEL, used.length ? Math.max(...used) + 1 : 1), '')]);
  };
  const changeLevel = (id, field, value) =>
    setLevels((list) => list.map((item) => (item.id === id ? { ...item, [field]: field === 'game' ? value : numberOrBlank(value) } : item)));
  const removeLevel = (id) => setLevels((list) => list.filter((item) => item.id !== id));

  const save = async () => {
    const problem = problemWith({ perGame, perLevel, levels });
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      setSaving(true);
      await api.put('/api/gcompris/settings/reward', {
        coinsPerGame: isBlank(perGame) ? 0 : Number(perGame),
        coinsPerNewLevel: isBlank(perLevel) ? 0 : Number(perLevel),
        levelCoins: rowsToSave(levels).map((item) => ({
          ...(item.game ? { activityName: item.game } : {}),
          ...(isBlank(item.level) ? {} : { level: Number(item.level) }),
          coins: Number(item.coins),
        })),
      });
      toast.success('GCompris coins saved');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save GCompris coins');
    } finally {
      setSaving(false);
    }
  };

  return (
    // Above the app header (z-index 1000), so it is dimmed too.
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="gcompris-coins-title" className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-start justify-between bg-gradient-to-r from-purple-700 to-fuchsia-600 px-6 py-5 text-white">
          <div>
            <h2 id="gcompris-coins-title" className="flex items-center gap-2 text-2xl font-bold"><Coins size={24} /> GCompris Coin Settings</h2>
            <p className="mt-1 text-sm text-purple-100">The coins students earn in GCompris games. Each reward is given only once.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-purple-700" aria-label="Close"><X size={22} /></button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-bold text-slate-700">
                Coins per game
                <input
                  type="number"
                  min="0"
                  max={MAX_COINS}
                  step="1"
                  placeholder="e.g. 10"
                  disabled={loading}
                  value={perGame}
                  onChange={(event) => setPerGame(numberOrBlank(event.target.value))}
                  className={inputClass}
                />
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-500">What a student gets for completing level 1 of a game.</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700">
                Coins per level up
                <input
                  type="number"
                  min="0"
                  max={MAX_COINS}
                  step="1"
                  placeholder="e.g. 2"
                  disabled={loading}
                  value={perLevel}
                  onChange={(event) => setPerLevel(numberOrBlank(event.target.value))}
                  className={inputClass}
                />
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-500">Added for each level after level 1.</p>
            </div>
          </div>
          <p className="rounded-lg bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-800">
            {exampleLevels(perGame, perLevel)}
          </p>

          <section aria-labelledby="gcompris-custom-levels">
            <div className="flex items-center justify-between gap-3">
              <h3 id="gcompris-custom-levels" className="text-sm font-bold text-slate-700">Custom coins for a specific game or level</h3>
              <button
                type="button"
                onClick={addLevel}
                disabled={loading || levels.length >= MAX_ROWS}
                className="inline-flex items-center gap-1.5 rounded-lg border border-purple-300 bg-purple-50 px-3 py-1.5 text-sm font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-50"
              >
                <Plus size={16} /> Add level
              </button>
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              These replace the coins of that level. With All games they count for that level in every game. Pick a game to give coins
              only in that game, and leave Level empty for every level of it. A game&apos;s own coins come first.
            </p>
            {levels.length > 0 && (
              <ul className="mt-3 space-y-2">
                {levels.map((item, i) => (
                  // A grid, not flex: the app-wide number-input width would squeeze the game picker.
                  <li key={item.id} className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <label className="min-w-0 text-xs font-semibold text-slate-600">
                      Game
                      <SearchableSelect
                        aria-label={`Game for custom level ${i + 1}`}
                        searchPlaceholder="Search games..."
                        value={item.game}
                        onChange={(event) => changeLevel(item.id, 'game', event.target.value)}
                        className={`${rowInputClass} truncate bg-white`}
                      >
                        <option value="">All games</option>
                        {item.game && !GCOMPRIS_ACTIVITIES.some((activity) => activity.name === item.game) && (
                          <option value={item.game}>{item.game}</option>
                        )}
                        {GAMES.map((activity) => (
                          <option key={activity.name} value={activity.name}>{activity.title}</option>
                        ))}
                      </SearchableSelect>
                    </label>
                    <label className="min-w-0 text-xs font-semibold text-slate-600">
                      Level
                      <input
                        type="number"
                        min="1"
                        max={MAX_LEVEL}
                        step="1"
                        placeholder={item.game ? 'Every level' : 'e.g. 5'}
                        aria-label={`Custom level ${i + 1}`}
                        value={item.level}
                        onChange={(event) => changeLevel(item.id, 'level', event.target.value)}
                        className={rowInputClass}
                      />
                    </label>
                    <label className="min-w-0 text-xs font-semibold text-slate-600">
                      Coins
                      <input
                        type="number"
                        min="0"
                        max={MAX_COINS}
                        step="1"
                        placeholder="e.g. 20"
                        aria-label={`Coins for custom level ${i + 1}`}
                        value={item.coins}
                        onChange={(event) => changeLevel(item.id, 'coins', event.target.value)}
                        className={rowInputClass}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeLevel(item.id)}
                      aria-label={`Remove custom level ${i + 1}`}
                      title="Remove"
                      className="mb-0.5 rounded-lg p-2 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={18} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-5 py-2 font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={save} disabled={loading || saving} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2 font-semibold text-white hover:bg-purple-700 disabled:bg-slate-400">
            <Save size={18} /> {saving ? 'Saving...' : 'Save Coins'}
          </button>
        </div>
      </div>
    </div>
  );
}
